import { ReactNode, useState } from "react";
import { Modal, ToastProvider } from "../ui";
import { useNextT } from "../i18n";
import { ContactForm } from "../contactUs/components/ContactForm";
import { TalkButtons } from "../contactUs/components/TalkButtons";
import type { ContactTopic } from "../contactUs/types";
import { useTranslation } from "react-i18next";
import { PublicIcon } from "./PublicIcon";
import s from "./public.module.scss";

const TOPICS: ContactTopic[] = ["question", "problem", "cannot_sign_in", "other"];

/** The Contact Us pop-up of the public pages (board 300): Call / WhatsApp / Text, then the ONE contact form in its public mode (the open, throttled call). */
export const PublicContactDialog = ({ open, onClose, topic = "question" }: { open: boolean; onClose: () => void; topic?: ContactTopic }) => {
  const { t } = useNextT();
  const [sent, setSent] = useState(false);
  return (
    <ToastProvider>
      <Modal open={open} onClose={() => { setSent(false); onClose(); }} title={t("contactUs.title")} size="md" onExited={() => setSent(false)}>
        <div className={s.contactBody}>
          {!sent && <TalkButtons />}
          <ContactForm mode="public" topics={TOPICS} initialTopic={topic} onSent={() => setSent(true)} onBack={() => { setSent(false); onClose(); }} />
        </div>
      </Modal>
    </ToastProvider>
  );
};

/** A quiet text button that opens the pop-up (the footer's "Contact us", the sign-in page's help link). */
export const ContactLink = ({ children, topic, variant = "foot" }: { children: ReactNode; topic?: ContactTopic; variant?: "foot" | "button" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={variant === "button" ? s.btnQuiet : s.footLink} onClick={() => setOpen(true)} aria-haspopup="dialog">{children}</button>
      <PublicContactDialog open={open} onClose={() => setOpen(false)} topic={topic} />
    </>
  );
};

/** v5 item 4: the help button on the sign-in windows is an icon AND a label (a chat bubble with "Contact us"), so it cannot be mistaken. Opens the same pop-up. */
export const ContactButton = ({ topic }: { topic?: ContactTopic }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={s.contactBtn} onClick={() => setOpen(true)} aria-haspopup="dialog"><PublicIcon name="chat" size={18} />{t("landing_nav.contact_us")}</button>
      <PublicContactDialog open={open} onClose={() => setOpen(false)} topic={topic} />
    </>
  );
};
